import { useContext, useEffect, useState } from "react";
import { Box } from "@mui/material";
import { getIn, useFormikContext } from "formik";
import { useTranslation } from "react-i18next";

import type { CuratedPlaceOfSafetyUnbranded } from "@thalamos/common";
import type {
  MhFormContext,
  PlaceOfSafetyFormBuilderField,
  PlaceOfSafetySchema,
} from "@thalamos/aspire-common";

import { config } from "../../config.js";
import { ManagedLocationsContext } from "../../Contexts.js";
import type { FieldProps } from "../../pages/FieldProps.js";
import { AddressField } from "../design-system/AddressField/AddressField.js";
import { FormLabel } from "../design-system/FormLabel.js";
import { HelperText } from "../design-system/HelperText/HelperText.js";
import { Typeahead } from "../design-system/Typeahead/Typeahead.js";
import type { SelectedTrust } from "./TrustPicker.js";
import { TrustPicker } from "./TrustPicker.js";

export type PlaceOfSafetyPickerProps<Data extends { [k: string]: any }> = {
  field: PlaceOfSafetyFormBuilderField<Data>;
  fieldProps: FieldProps<Data>;
};

type PlaceOfSafetyAndCombined = CuratedPlaceOfSafetyUnbranded & {
  combined: string;
};

export function PlaceOfSafetyPicker<Data extends { [k: string]: any }>({
  field,
  fieldProps,
}: PlaceOfSafetyPickerProps<Data>) {
  const { submitCount } = useFormikContext();

  const selected = fieldProps.values[field.field] as
    | PlaceOfSafetySchema
    | undefined;

  const { t } = useTranslation();

  const managedLocationsContext = useContext(ManagedLocationsContext);

  const selectedPlaceOfSafety =
    selected?.placeOfSafetyId &&
    managedLocationsContext.curatedPlacesOfSafety.find(
      (pos) => pos.id === selected.placeOfSafetyId,
    );

  const notListedTrust = managedLocationsContext.trusts.find(
    (trust) => trust.notListed,
  );
  const trustUnknown = selected?.trustId === notListedTrust?.id;

  const placesOfSafety: PlaceOfSafetyAndCombined[] =
    managedLocationsContext.curatedPlacesOfSafety
      .sort((a, b) => a.name.localeCompare(b.name))
      .map((pos) => {
        const matchedTrust = managedLocationsContext.trusts.find(
          (trust) => trust.id === pos.trustId,
        );

        return {
          ...pos,
          trustName: matchedTrust?.name,
          combined: `${pos.name}, ${pos.address}`,
        };
      });

  const [address, setAddress] = useState({
    address: selected?.address ?? "",
    postalCode: selected?.postalCode ?? "",
    isConfirmed: selected?.isConfirmed ?? false,
  });

  // Sync trust from curated POS when the form data has a placeOfSafetyId
  // but is missing trustId (e.g. prefilled drafts that omitted trust info)
  useEffect(() => {
    if (
      selectedPlaceOfSafety &&
      selectedPlaceOfSafety.trustId &&
      !selected?.trustId
    ) {
      const matchedTrust = managedLocationsContext.trusts.find(
        (trust) => trust.id === selectedPlaceOfSafety.trustId,
      );
      fieldProps.setValues({
        ...fieldProps.values,
        [field.field]: {
          ...selected,
          trustId: selectedPlaceOfSafety.trustId,
          trustName: matchedTrust?.name,
        },
      });
    }
  }, [selectedPlaceOfSafety, selected?.trustId]);

  // Sync address to form on submit if trust is unknown (ad-hoc entry)
  useEffect(() => {
    if (submitCount > 0 && !selected?.trustId && !trustUnknown) {
      fieldProps.setFieldTouched(`${field.field}.trustId`, true, true);
    }
  }, [submitCount, selected?.trustId, trustUnknown]);

  // Update form state of address from local component state
  useEffect(() => {
    if (
      address.address !== (selected?.address ?? "") ||
      address.postalCode !== (selected?.postalCode ?? "") ||
      address.isConfirmed !== (selected?.isConfirmed ?? false)
    ) {
      fieldProps.setFieldTouched(`${field.field}.address`, true, false);
      fieldProps.setFieldTouched(`${field.field}.postalCode`, true, false);

      const newValues = {
        ...fieldProps.values,
        [field.field]: { ...(selected ?? {}), ...address },
      };
      fieldProps.setValues(newValues);
    }
  }, [JSON.stringify(address)]);

  // Set default value if there is no value and the field definition has a defaultFn
  useEffect(() => {
    if (
      !selected?.name &&
      !selected?.address &&
      !selected?.postalCode &&
      !selected?.isConfirmed &&
      field.defaultFn
    ) {
      const defaultValue = field.defaultFn(
        fieldProps.values,
        fieldProps.context as MhFormContext,
      );
      if (defaultValue) {
        setAddress({
          address: defaultValue?.address ?? "",
          postalCode: defaultValue?.postalCode ?? "",
          isConfirmed: false,
        });
        fieldProps.setValues({
          ...fieldProps.values,
          [field.field]: { ...defaultValue, isConfirmed: false },
        });
      }
    } else if (selected === undefined) {
      setAddress({
        address: "",
        postalCode: "",
        isConfirmed: false,
      });
      fieldProps.setValues({
        ...fieldProps.values,
        [field.field]: { name: "", isConfirmed: false },
      });
    }
  }, [JSON.stringify(selected)]);

  const nameErrors =
    getIn(fieldProps.touched, `${field.field}.name`) &&
    getIn(fieldProps.errors, `${field.field}.name`);

  const addressErrors =
    getIn(fieldProps.touched, `${field.field}.address`) &&
    getIn(fieldProps.errors, `${field.field}.address`);

  const postalCodeErrors =
    getIn(fieldProps.touched, `${field.field}.postalCode`) &&
    getIn(fieldProps.errors, `${field.field}.postalCode`);

  const confirmErrors =
    getIn(fieldProps.touched, `${field.field}.isConfirmed`) &&
    getIn(fieldProps.errors, `${field.field}.isConfirmed`);

  const trustIdErrors =
    getIn(fieldProps.touched, `${field.field}.trustId`) &&
    getIn(fieldProps.errors, `${field.field}.trustId`);

  return (
    <>
      {field.label && (
        <FormLabel label={field.label} error={false} tooltip={field.tooltip} />
      )}
      <Typeahead
        testId={`${field.field}.name`}
        label="Place of Safety Name"
        enableFuse={true}
        placeholder={t("forms.placeOfSafetyPicker.typeaheadPlaceholder")}
        errorMessage={
          nameErrors
            ? getIn(fieldProps.errors, `${field.field}.name`)
            : undefined
        }
        showHelperText={!!nameErrors}
        name={`${field.field}.name`}
        inputValue={selected?.name ?? ""}
        optionsKey={"combined"}
        onInputChange={(value: string | null) => {
          fieldProps.setFieldTouched(`${field.field}.name`, true, false);

          if (value) {
            const placeOfSafety = placesOfSafety.find(
              (pos) => pos.combined === value,
            );
            const matchedTrust = managedLocationsContext.trusts.find(
              (trust) => trust.id === placeOfSafety?.trustId,
            );

            if (placeOfSafety) {
              setAddress({
                address: placeOfSafety.address,
                postalCode: placeOfSafety.postalCode,
                isConfirmed: true,
              });
            } else {
              setAddress((prevState) => {
                return {
                  ...prevState,
                  isConfirmed: false,
                };
              });
            }

            fieldProps.setValues({
              ...fieldProps.values,
              [field.field]: {
                ...selected,
                ...(placeOfSafety
                  ? {
                      name: placeOfSafety.name,
                      placeOfSafetyId: placeOfSafety.id,
                      trustId: placeOfSafety.trustId,
                      trustName: matchedTrust?.name,
                      isConfirmed: true,
                    }
                  : {
                      name: value,
                      placeOfSafetyId: undefined,
                      trustId: undefined,
                      trustName: undefined,
                    }),
              },
            });
          } else {
            setAddress({
              address: "",
              postalCode: "",
              isConfirmed: false,
            });
            fieldProps.setValues({
              ...fieldProps.values,
              [field.field]: { name: "" },
            });
          }
        }}
        options={placesOfSafety}
        getOptionLabel={(pos: PlaceOfSafetyAndCombined | string) => {
          if (typeof pos === "string") {
            return pos;
          } else {
            return pos.combined;
          }
        }}
      />

      <Box sx={{ mb: 3 }}>
        {!field.hideAddress && (
          <>
            <AddressField
              testId={`${field.field}.address`}
              placeholder={{
                address: t("forms.placeOfSafetyPicker.addressPlaceholder"),
                postalCode: t(
                  "forms.placeOfSafetyPicker.postalCodePlaceholder",
                ),
              }}
              addressLabel={t("forms.placeOfSafetyPicker.addressLabel")}
              postalLabel={t("forms.placeOfSafetyPicker.postalLabel")}
              subtextIcon="error"
              showHelperText={
                addressErrors || postalCodeErrors || confirmErrors
              }
              errors={{
                address: addressErrors,
                postalCode: postalCodeErrors,
                isConfirmed: confirmErrors,
              }}
              googleMapsApiKey={config.googleMapsApiKey}
              addressCheckBoxLabel={t(
                "forms.placeOfSafetyPicker.addressCheckBoxLabel",
              )}
              name={field.field}
              value={address}
              onChange={setAddress}
            />
            {field.afterLabel && <HelperText subtext={field.afterLabel} />}
          </>
        )}

        {!selectedPlaceOfSafety || !selectedPlaceOfSafety.trustId ? (
          <>
            <Box sx={{ mb: "1rem", mt: "1rem" }}>
              <TrustPicker
                value={
                  selected?.trustId || selected?.trustName
                    ? {
                        id: selected.trustId,
                        name: selected.trustName ?? "",
                        isNotListed: trustUnknown,
                      }
                    : null
                }
                onChange={(trust: SelectedTrust) => {
                  fieldProps.setFieldTouched(
                    `${field.field}.name`,
                    true,
                    false,
                  );

                  if (trust) {
                    fieldProps.setValues({
                      ...fieldProps.values,
                      [field.field]: {
                        ...selected,
                        trustId: trust.id,
                        trustName: trust.name,
                      },
                    });
                  } else {
                    fieldProps.setValues({
                      ...fieldProps.values,
                      [field.field]: {
                        ...selected,
                        trustId: undefined,
                        trustName: "",
                      },
                    });
                    fieldProps.setFieldTouched(
                      `${field.field}.trustId`,
                      true,
                      true,
                    );
                  }
                }}
                allowNotListed={true}
                errorMessage={
                  typeof trustIdErrors === "string" ? trustIdErrors : undefined
                }
              />
            </Box>
            <HelperText
              noteMessage={t("forms.placeOfSafetyPicker.trustAutoSelected")}
            />
          </>
        ) : (
          <Box>
            <Box sx={{ mb: "1rem", mt: "1rem", fontWeight: "bold" }}>
              Trust: {selected?.trustName}
            </Box>

            <HelperText
              noteMessage={t("forms.placeOfSafetyPicker.trustAutoSelected")}
            />
          </Box>
        )}
      </Box>
    </>
  );
}
